import { useState, useEffect } from 'react';
import { authService } from '../../services/api';
import { useNotification } from '../Notifications/NotificationContext';
import { hasConnectedCard, latamCurrenciesForBot } from '../../utils/gateways';
import { inLatamOrder, marketOf, normalizeLatamBase, normalizeMode } from '../../utils/market';
import MarketSegmentedControl from './MarketSegmentedControl';
import LatamSettings from './LatamSettings';

/**
 * Mercado/moeda-base do BOT (Brasil/BRL, Global/USD ou LATAM/MXN), no cabeçalho de editar
 * bot. Usa o MarketSegmentedControl e grava a troca direto na API:
 * otimista, volta atrás se a API recusar, e avisa as outras abas
 * (`bot-currency-changed`, ouvido pelo useBotCurrency).
 *
 * No LATAM mostra ao lado um botão de ajustes (LatamSettings) com as moedas
 * que o bot aceita (`latam_currencies`) e a moeda em que os valores são
 * escritos (`latam_base_currency`).
 */
export default function CurrencyModeToggle({ botId }) {
    const { showError, showSuccess } = useNotification();
    const [mode, setMode] = useState(null); // null até ler o bot: não chuta Brasil
    const [hasCard, setHasCard] = useState(null);
    const [latamAvailable, setLatamAvailable] = useState(null); // países que a XPag cobra
    const [countries, setCountries] = useState([]);              // moedas marcadas no bot
    const [base, setBase] = useState('MXN');                     // moeda dos valores no LATAM
    const [saving, setSaving] = useState(false);

    const readPixConfig = () => authService.bots.edit_bot(botId).then(({ data }) => data.bot?.pix_config || {});

    useEffect(() => {
        if (!botId) return;
        readPixConfig()
            .then((pc) => {
                setMode(normalizeMode(pc.currency_mode));
                setCountries(inLatamOrder(pc.latam_currencies));
                setBase(normalizeLatamBase(pc.latam_base_currency));
            })
            .catch(() => setMode(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [botId]);

    useEffect(() => {
        authService.gateways.list_gateways()
            .then((res) => {
                setHasCard(hasConnectedCard(res.data?.data));
                setLatamAvailable(latamCurrenciesForBot(res.data?.data, botId));
            })
            .catch(() => { setHasCard(false); setLatamAvailable([]); });
    }, [botId]);

    // Troca feita em outro lugar (outra instância deste controle).
    useEffect(() => {
        const onChanged = (e) => {
            if (!botId || String(e.detail?.botId) !== String(botId)) return;
            if (e.detail?.mode) setMode(normalizeMode(e.detail.mode));
            if (e.detail?.base) setBase(normalizeLatamBase(e.detail.base));
        };
        window.addEventListener('bot-currency-changed', onChanged);
        return () => window.removeEventListener('bot-currency-changed', onChanged);
    }, [botId]);

    const change = async (target) => {
        if (!botId) return;
        const prev = mode;
        setMode(target);
        setSaving(true);
        try {
            const { data } = await authService.bots.edit_bot_submit(botId, { currency_mode: target });
            if (data && data.success === false) throw new Error(data.error || 'Erro ao atualizar moeda-base');
            window.dispatchEvent(new CustomEvent('bot-currency-changed', { detail: { botId, mode: target } }));
            const market = marketOf(target, base);
            showSuccess(`Modo ${market.label} (${market.code}) ativado`);
            // Ao entrar no LATAM o backend liga os países: mostra os que ficaram.
            if (target === 'mxn') readPixConfig().then((pc) => setCountries(inLatamOrder(pc.latam_currencies))).catch(() => {});
        } catch (err) {
            setMode(prev);
            showError(err?.response?.data?.error || err?.message || 'Erro ao atualizar moeda-base');
        } finally {
            setSaving(false);
        }
    };

    const changeCountries = async (next) => {
        const prev = countries;
        setCountries(next);
        setSaving(true);
        try {
            const { data } = await authService.bots.edit_bot_submit(botId, { latam_currencies: next });
            if (data && data.success === false) throw new Error(data.error || 'Erro ao salvar as moedas');
        } catch (err) {
            setCountries(prev);
            showError(err?.response?.data?.error || err?.message || 'Erro ao salvar as moedas');
        } finally {
            setSaving(false);
        }
    };

    const changeBase = async (next) => {
        const prev = base;
        setBase(next);
        setSaving(true);
        try {
            const { data } = await authService.bots.edit_bot_submit(botId, { latam_base_currency: next });
            if (data && data.success === false) throw new Error(data.error || 'Erro ao salvar a moeda dos valores');
            window.dispatchEvent(new CustomEvent('bot-currency-changed', { detail: { botId, base: next } }));
            showSuccess(`Valores do bot em ${next}`);
        } catch (err) {
            setBase(prev);
            showError(err?.response?.data?.error || err?.message || 'Erro ao salvar a moeda dos valores');
        } finally {
            setSaving(false);
        }
    };

    const hasLatam = latamAvailable === null ? null : latamAvailable.length > 0;

    return (
        <div className="inline-flex flex-wrap items-center gap-2">
            <MarketSegmentedControl value={mode} onChange={change} hasCard={hasCard} hasLatam={hasLatam}
                allowLatam saving={saving} subject="bot" />
            {mode === 'mxn' && (
                <LatamSettings available={latamAvailable || []} currencies={countries}
                    onCurrenciesChange={changeCountries} base={base} onBaseChange={changeBase} saving={saving} />
            )}
        </div>
    );
}
